/**
 * formatters.ts
 *
 * Pure formatting utilities for transforming strings and values
 */

import { logger } from './logger';

/** Convert 'genesyscloud_auth_role' → 'Auth Role' */
export function toFriendlyName(resourceType: string): string {
  try {
    if (!resourceType || typeof resourceType !== 'string') {
      logger.warn('toFriendlyName received invalid input', {
        type: typeof resourceType,
        value: String(resourceType)
      });
      return 'Unknown';
    }

    return resourceType
      .replace(/^genesyscloud_/, '')
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  } catch (error) {
    logger.error('Error in toFriendlyName', {
      error: error instanceof Error ? error.message : String(error),
      input: String(resourceType)
    });
    return 'Unknown';
  }
}

/** Format any JSON value as a human-readable string */
export function formatValue(value: unknown): string {
  try {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(', ');
    }
    return JSON.stringify(value);
  } catch (error) {
    logger.warn('Error formatting value', {
      error: error instanceof Error ? error.message : String(error),
      type: typeof value
    });
    return '[Unable to format]';
  }
}

/** Convert camelCase to Title Case with spaces */
export function camelCaseToTitleCase(str: string): string {
  try {
    if (!str || typeof str !== 'string') {
      return String(str);
    }
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (match) => match.toUpperCase())
      .trim();
  } catch (error) {
    logger.warn('Error in camelCaseToTitleCase', {
      error: error instanceof Error ? error.message : String(error),
      input: String(str)
    });
    return String(str);
  }
}

/**
 * Convert snake_case property names to Title Case with spaces
 * Examples:
 *   division_id → Division Id
 *   interruptible_media_types → Interruptible Media Types
 */
export function formatPropertyName(propertyName: string): string {
  try {
    if (!propertyName || typeof propertyName !== 'string') {
      return String(propertyName);
    }
    return propertyName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } catch (error) {
    logger.warn('Error in formatPropertyName', {
      error: error instanceof Error ? error.message : String(error),
      input: String(propertyName)
    });
    return String(propertyName);
  }
}

/**
 * Extract resource name from Terraform-style reference strings
 * Examples:
 *   ${genesyscloud_auth_division.Demo.id} → Demo
 *   ${genesyscloud_auth_division.Development.id} → Development
 *   genesyscloud_user.Admin.id → Admin
 */
export function extractResourceName(value: string): string {
  try {
    if (!value || typeof value !== 'string') {
      return String(value);
    }
    // Match the word before .id (with optional closing brace)
    const match = value.match(/\.([^.]+)\.id\}?$/);
    return match?.[1] ?? value;
  } catch (error) {
    logger.warn('Error in extractResourceName', {
      error: error instanceof Error ? error.message : String(error),
      input: String(value)
    });
    return String(value);
  }
}

// ─── Timezone helpers ──────────────────────────────────────────────────────

/** Returns the user's IANA timezone string from the browser (e.g. 'America/Chicago') */
export function getUserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    logger.warn('Error getting user timezone', {
      error: error instanceof Error ? error.message : String(error)
    });
    return 'UTC';
  }
}

/**
 * Returns a short timezone abbreviation for the user's local timezone (e.g. 'CST', 'CDT').
 * Falls back to the full IANA name if the short form is unavailable.
 */
export function getLocalTimezoneAbbr(): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value ?? getUserTimeZone()
    );
  } catch (error) {
    logger.warn('Error getting timezone abbreviation', {
      error: error instanceof Error ? error.message : String(error)
    });
    return 'UTC';
  }
}

/**
 * Converts a UTC 24-hour hour + minute into the user's local 12-hour time.
 * Uses today's date so DST offsets are correctly reflected.
 */
export function utcToLocal12h(
  utcHour: number,
  utcMinute: number
): { hour12: number; minute: number; ampm: 'AM' | 'PM'; dayOffset: number } {
  const now = new Date();
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHour, utcMinute, 0, 0)
  );
  const localHour = d.getHours();
  const ampm: 'AM' | 'PM' = localHour >= 12 ? 'PM' : 'AM';
  const hour12 = localHour % 12 || 12;
  const localDayKey = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000;
  const utcDayKey = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000;

  return { hour12, minute: d.getMinutes(), ampm, dayOffset: localDayKey - utcDayKey };
}

/**
 * Converts a local 12-hour time to UTC hours and minutes for use in cron expressions.
 * Uses today's date so DST offsets are correctly reflected.
 */
export function localToUtc24h(
  hour12: number,
  minute: number,
  ampm: 'AM' | 'PM'
): { utcHour: number; utcMinute: number; dayOffset: number } {
  const localHour24 =
    ampm === 'PM' ? (hour12 === 12 ? 12 : hour12 + 12) : hour12 === 12 ? 0 : hour12;
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), localHour24, minute, 0, 0);
  const localDayKey = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000;
  const utcDayKey = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000;

  return {
    utcHour: d.getUTCHours(),
    utcMinute: d.getUTCMinutes(),
    dayOffset: utcDayKey - localDayKey
  };
}

/**
 * Formats a UTC ISO timestamp string for display in the user's local timezone.
 * Returns a locale string with the timezone abbreviation appended, e.g. "5/4/2026, 9:00:00 AM CDT".
 */
export function formatUtcTimestampLocal(utcIso: string): string {
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return utcIso;
  return `${d.toLocaleString()} ${getLocalTimezoneAbbr()}`;
}

/**
 * Formats a region code into a user-friendly display name
 * @param region - AWS region code (e.g., "us-east-1") or Genesys Cloud domain
 * @returns Formatted region string with friendly name and code
 */
export function formatRegion(region: string | undefined): string {
  // Handle undefined or empty regions
  if (!region) {
    return 'Region not specified';
  }

  // AWS region mappings
  const regionMap: Record<string, string> = {
    // US regions
    'us-east-1': 'US East',
    'us-east-2': 'US East',
    'us-west-1': 'US West',
    'us-west-2': 'US West',

    // Genesys Cloud domain-based regions (if API returns these instead)
    'mypurecloud.com': 'US East',
    'usw2.pure.cloud': 'US West'
  };

  const friendly = regionMap[region.toLowerCase()] || region;
  return `${friendly} (${region})`;
}
